import { useEffect, useRef } from 'react';

export type UseSeedFieldOnceArgs = Readonly<{
  /** The value to seed in, once it is known. Falsy means "not known yet". */
  seedValue: string | undefined;
  /** The field's current value. */
  currentValue: string | undefined;
  /** Whether the guest has focused and left the field. */
  isTouched: boolean | undefined;
  /** Applies the seed. */
  onSeed: (value: string) => void;
}>;

/**
 * Fills a form field from an async source exactly once, and only if it gets there before
 * the guest does.
 *
 * Formik snapshots `initialValues` on mount, so a value that arrives later has to be
 * applied by hand. The obvious guard — "seed while the field is empty and untouched" — is
 * wrong: `touched` is only set on blur, so a guest who selects a prefilled value and
 * deletes it has an empty, untouched field, and the seed lands again the moment they hit
 * backspace. The field appears to refuse to be cleared.
 *
 * The opportunity to seed is therefore spent as soon as it is taken *or* as soon as the
 * guest reaches the field first, whichever happens earlier. After that this never writes
 * again, so clearing, retyping and blanking all behave the way the guest expects.
 */
export const useSeedFieldOnce = ({ seedValue, currentValue, isTouched, onSeed }: UseSeedFieldOnceArgs): void => {
  const isSpent = useRef(false);

  useEffect(() => {
    if (isSpent.current) {
      return;
    }

    // The guest got here first — either they typed something or they have visited the
    // field. Either way the seed is no longer wanted.
    if (currentValue || isTouched) {
      isSpent.current = true;
      return;
    }

    if (seedValue) {
      onSeed(seedValue);
      isSpent.current = true;
    }
  }, [seedValue, currentValue, isTouched, onSeed]);
};
